import { useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import GenieLoader from "@/components/ui/GenieLoader";
import { useAuth } from "./AuthProvider";

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: string[];
}

export default function RoleProtectedRoute({ children, allowedRoles }: RoleProtectedRouteProps) {
  const { user, userProfile, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || userProfile === undefined) return;
    if (!userProfile || !userProfile.role || !allowedRoles.includes(userProfile.role)) {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [userProfile, allowedRoles, navigate, loading]);

  if (loading || (user && userProfile === undefined)) {
    // Show loading if initial auth check is running,
    // or if we have a user but haven't checked for a profile yet.
    return <GenieLoader size="fullscreen" />;
  }

  if (!user) {
    // User not logged in, redirect to sign-in page
    // Pass the current location so we can redirect back after login
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  if (!userProfile || !userProfile.role || !allowedRoles.includes(userProfile.role)) {
    console.warn(
      `User with role '${userProfile?.role}' attempted to access route '${
        location.pathname
      }' restricted to roles: ${allowedRoles.join(", ")}`
    );
    return null;
  }

  return <>{children}</>;
}
